import { array, boolean, object, string } from 'yup'

import { CustomFieldFieldsFragment } from '~/graphql/custom-field'
import { CustomFieldEntityType, CustomFieldType } from '~/types/graphql'

import { parseCustomFieldValues } from './custom-field-value-utils'
import { typedOptions } from './ts-utils'

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  [CustomFieldType.Text]: 'Text',
  [CustomFieldType.Textarea]: 'Paragraph',
  [CustomFieldType.Boolean]: 'Checkbox',
  [CustomFieldType.Select]: 'Dropdown',
  [CustomFieldType.MultiSelect]: 'Multiple choice',
  [CustomFieldType.Number]: 'Number',
  [CustomFieldType.Date]: 'Date',
}

// we are omiting number and date for now
// as we need to add validation for those first
const CREATABLE_CUSTOM_FIELD_TYPES: CustomFieldType[] = [
  CustomFieldType.Text,
  CustomFieldType.Textarea,
  CustomFieldType.Boolean,
  CustomFieldType.Select,
  CustomFieldType.MultiSelect,
]

export const customFieldTypeOptions = typedOptions(CUSTOM_FIELD_TYPE_LABELS)

export const creatableCustomFieldTypeOptions = customFieldTypeOptions.filter(
  option => CREATABLE_CUSTOM_FIELD_TYPES.includes(option.value)
)

export const customFieldTypeHasOptions = (type: CustomFieldType) =>
  type === CustomFieldType.Select || type === CustomFieldType.MultiSelect

export const CUSTOM_FIELD_ENTITY_LABELS: Record<CustomFieldEntityType, string> =
  {
    [CustomFieldEntityType.Contact]: 'Contact',
    [CustomFieldEntityType.Institution]: 'Institution',
    [CustomFieldEntityType.Order]: 'Donation',
    [CustomFieldEntityType.Ticket]: 'Ticket',
  }

export type PageCustomField = CustomFieldFieldsFragment

export const customFieldName = (
  field: PageCustomField,
  path = 'customFields'
) => `${path}.${field.id}`

export const buildCustomFieldsInitialValues = (
  fields: PageCustomField[]
): Record<string, unknown> =>
  Object.fromEntries(
    fields.map(field => {
      if (field.type === CustomFieldType.Boolean) {
        return [field.id, false]
      }

      if (field.type === CustomFieldType.MultiSelect) {
        return [field.id, []]
      }

      return [field.id, '']
    })
  )

// The stored answers arrive as a list; a form holds them keyed by field id.
// A removed option stays in the answer already stored, and the API rejects a
// value that is no longer an option. Such an answer keeps the empty default
// below, or an attendee edit fails on a question the donor never touched.
export const buildAnswerValues = (
  customFields: PageCustomField[],
  serialized?: string | null
): Record<string, unknown> => {
  const values = buildCustomFieldsInitialValues(customFields)
  const fieldsById = new Map(customFields.map(field => [field.id, field]))

  for (const answer of parseCustomFieldValues(serialized)) {
    const field = fieldsById.get(answer.id)

    if (!field) {
      continue
    }

    const options = field.options ?? []

    if (field.type === CustomFieldType.MultiSelect) {
      if (Array.isArray(answer.value)) {
        values[field.id] = answer.value.filter(item => options.includes(item))
      }

      continue
    }

    if (
      field.type === CustomFieldType.Select &&
      !options.includes(String(answer.value))
    ) {
      continue
    }

    values[field.id] = answer.value
  }

  return values
}

export const buildCustomFieldsSchema = (fields: PageCustomField[]) =>
  object(
    Object.fromEntries(
      fields.map(field => {
        const message = `${field.label} is required`

        if (field.type === CustomFieldType.Boolean) {
          return [
            field.id,
            field.required
              ? boolean().oneOf([true], message).required(message)
              : boolean(),
          ]
        }

        if (field.type === CustomFieldType.MultiSelect) {
          return [field.id, field.required ? array().min(1, message) : array()]
        }

        // Number posts a string, so `mixed().required()` would accept the empty
        // initial value.
        return [
          field.id,
          field.required ? string().trim().required(message) : string(),
        ]
      })
    )
  )

// The API takes a serialized JSON object: the value union varies per type.
export const serializeCustomFieldValues = (
  values: Record<string, unknown> | undefined
) => (values && Object.keys(values).length ? JSON.stringify(values) : undefined)
